/**
 * /settings/api-costs — Vendor spend watch UI.
 *
 * Top: active alerts strip (red for critical, amber for warning).
 * Middle: accounts table with MTD spend, forecast vs budget, runway, alerts.
 * Right rail: "Add account" + "Sync all" actions.
 *
 * Talks to:
 *   GET   /api/vendor-watch/accounts            → list with snapshots + alerts
 *   POST  /api/vendor-watch/accounts            → create
 *   PATCH /api/vendor-watch/accounts/{id}       → update balance / thresholds
 *   POST  /api/vendor-watch/accounts/{id}/sync  → manual sync one
 *   POST  /api/vendor-watch/sync-all            → sync everything
 *   GET   /api/vendor-watch/alerts              → all active alerts
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  LinearProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PeopleIcon from "@mui/icons-material/People";

import { api, errorMessage } from "@/lib/api";

// ─── types ────────────────────────────────────────────────────────────────

type AlertOut = {
  alert_type: string;
  severity: "info" | "warning" | "critical";
  triggered_at: string;
  message: string;
};

type AutoRechargeView = {
  charges_this_month: number | null;
  days_per_charge: number | null;
  forecast_month_cents: number | null;
  variance_vs_budget_pct: number | null;
};

type SnapshotOut = {
  period_start: string;
  period_end: string;
  amount_cents: number;
  daily_burn_cents: number;
  projected_days_to_zero: number | null;
  captured_at: string;
  next_billing_date: string | null;
  _adapter_variant: string | null;
  _warning: string | null;
};

type Recommendation = {
  vendor: string | null;
  account_label: string | null;
  category:
    | "usage_pattern"
    | "wasted_capacity"
    | "commitment_savings"
    | "caching_opportunity"
    | "cheaper_tier"
    | "mission_critical_risk"
    | "forecast_anomaly"
    | "billing_hygiene";
  title: string;
  rationale: string;
  estimated_monthly_savings_cents: number | null;
  confidence: "high" | "medium" | "low";
  severity: "info" | "warning" | "critical";
  action: string | null;
};

type AnalysisResult = {
  summary: string;
  estimated_total_savings_cents: number;
  recommendations: Recommendation[];
  generated_at: string;
  model: string;
  accounts_analyzed: number;
};

type AccountOut = {
  id: string;
  vendor: string;
  account_label: string;
  is_mission_critical: boolean;
  is_active: boolean;
  credit_balance_cents: number | null;
  credit_balance_updated_at: string | null;
  auto_recharge_amount_cents: number | null;
  monthly_budget_cents: number | null;
  auto_recharge_view: AutoRechargeView;
  thresholds: {
    low_balance_cents: number;
    min_days_runway: number;
    mtd_threshold_cents: number;
  };
  last_synced_at: string | null;
  last_sync_status: "never" | "ok" | "error";
  last_sync_error: string | null;
  latest_snapshot: SnapshotOut | null;
  active_alerts: AlertOut[];
};

type MemberRow = {
  name: string;
  email: string;
  role: string;
  spend_cents: number;
  included_spend_cents: number;
  removed_at: string | null;
};

// ─── MembersDrawer ────────────────────────────────────────────────────────

const VENDORS_WITH_MEMBERS = ["cursor"];

function MembersDrawer({
  account,
  onClose,
}: {
  account: AccountOut;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["vendor-watch", "members", account.id],
    queryFn: () =>
      api<{ members: MemberRow[]; captured_at: string | null }>(
        `/api/vendor-watch/accounts/${account.id}/members`
      ),
  });

  const members = data?.members ?? [];
  const total = members.reduce((s, m) => s + m.spend_cents, 0);

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100vw", sm: 620 }, display: "flex", flexDirection: "column" } }}>
      {/* Fixed header */}
      <Box sx={{ p: 3, pb: 2, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Team spend breakdown</Typography>
            <Typography variant="caption" color="text.secondary">
              {account.vendor}/{account.account_label}
              {data?.captured_at ? ` · synced ${ago(data.captured_at)}` : ""}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>✕</span>
          </IconButton>
        </Stack>

        {members.length > 0 && (
          <Stack direction="row" spacing={4} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Total MTD</Typography>
              <Typography variant="h5">{dollars(total)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Active members</Typography>
              <Typography variant="h5">{members.filter(m => !m.removed_at).length}</Typography>
            </Box>
          </Stack>
        )}
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflow: "auto", p: 0 }}>
        {isLoading && <LinearProgress />}
        {error && <Alert severity="error" sx={{ m: 2 }}>Failed to load member breakdown.</Alert>}
        {!isLoading && members.length === 0 && (
          <Alert severity="info" sx={{ m: 2 }}>
            No member-level data yet — sync the account to populate.
          </Alert>
        )}

        {members.length > 0 && (
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow sx={{ "& th": { py: 1.25, bgcolor: "background.default" } }}>
                <TableCell sx={{ width: "42%" }}>Member</TableCell>
                <TableCell sx={{ width: "13%" }}>Role</TableCell>
                <TableCell align="right" sx={{ width: "17%" }}>Spend</TableCell>
                <TableCell align="right" sx={{ width: "15%" }}>Included</TableCell>
                <TableCell align="right" sx={{ width: "13%" }}>Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m, i) => {
                const pctOfTotal = total > 0 ? (m.spend_cents / total) * 100 : 0;
                const isRemoved = !!m.removed_at;
                return (
                  <TableRow key={i} sx={{ opacity: isRemoved ? 0.5 : 1, "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ py: 1.25 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.name}</Typography>
                        {isRemoved && <Chip size="small" label="removed" sx={{ fontSize: "0.6rem", height: 18 }} />}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", maxWidth: "100%" }}>
                        {m.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.25 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                        {m.role || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.25 }}>
                      <Typography variant="body2">{dollars(m.spend_cents)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        {dollars(m.included_spend_cents)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.25 }}>
                      <Typography variant="caption" sx={{ display: "block" }}>{pctOfTotal.toFixed(1)}%</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={pctOfTotal}
                        sx={{ width: "100%", height: 4, borderRadius: 2, mt: 0.5 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>
    </Drawer>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────

const dollars = (cents: number | null | undefined) =>
  cents == null ? "—" : `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const pct = (v: number | null | undefined) =>
  v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;

const ago = (iso: string | null) => {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const severityColor = (sev: string) =>
  sev === "critical" ? "error" : sev === "warning" ? "warning" : "info";

function BillingDateCell({ dateStr }: { dateStr: string }) {
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysUntil = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  const label = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (daysUntil < 0) {
    return (
      <Box>
        <Typography variant="body2" color="error.main">{label}</Typography>
        <Typography variant="caption" color="error.main">overdue</Typography>
      </Box>
    );
  }
  if (daysUntil <= 7) {
    return (
      <Box>
        <Typography variant="body2" color="warning.main" fontWeight={600}>{label}</Typography>
        <Typography variant="caption" color="warning.main">in {daysUntil}d ⚠</Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="caption" color="text.secondary">in {daysUntil}d</Typography>
    </Box>
  );
}

// ─── Vendor catalog ───────────────────────────────────────────────────────

const VENDOR_CATALOG = [
  {
    id: "anthropic",
    name: "Anthropic",
    category: "AI",
    description: "Claude API usage-based billing via Admin API key.",
    domain: "anthropic.com",
  },
  {
    id: "openai",
    name: "OpenAI",
    category: "AI",
    description: "GPT, Whisper, DALL·E costs via Admin API key.",
    domain: "openai.com",
  },
  {
    id: "aws",
    name: "AWS",
    category: "Cloud",
    description: "All AWS services — EC2, S3, RDS — via Cost Explorer API.",
    domain: "aws.amazon.com",
  },
  {
    id: "cloudflare",
    name: "Cloudflare",
    category: "Network",
    description: "Plans, Argo, Cache Reserve via account subscriptions API.",
    domain: "cloudflare.com",
  },
  {
    id: "google_ai_studio",
    name: "Google AI Studio",
    category: "AI",
    description: "Gemini API (consumer tier) via BigQuery billing export.",
    domain: "aistudio.google.com",
  },
  {
    id: "google_vertex_ai",
    name: "Google Vertex AI",
    category: "AI",
    description: "Vertex AI enterprise Gemini via Cloud Monitoring.",
    domain: "cloud.google.com",
  },
  {
    id: "cursor",
    name: "Cursor",
    category: "AI",
    description: "Seat + overage spend via Cursor Admin API.",
    domain: "cursor.com",
  },
  {
    id: "vapi",
    name: "VAPI.ai",
    category: "AI",
    description: "Voice AI call spend via VAPI Analytics API.",
    domain: "vapi.ai",
  },
  {
    id: "upwork",
    name: "Upwork",
    category: "Labor",
    description: "Contractor & freelancer spend via Upwork Financial Reports API.",
    domain: "upwork.com",
  },
  {
    id: "stripe",
    name: "Stripe",
    category: "Payments",
    description: "Track Stripe processing fees via Balance Transactions API.",
    domain: "stripe.com",
  },
  {
    id: "chargebee",
    name: "Chargebee",
    category: "Billing",
    description: "Subscription billing spend and fees via Chargebee API.",
    domain: "chargebee.com",
  },
  {
    id: "shopify",
    name: "Shopify",
    category: "E-commerce",
    description: "Monthly Shopify plan fees and app charges — Edge reads your billing page.",
    domain: "shopify.com",
  },
  {
    id: "__custom__",
    name: "Any other vendor",
    category: "Custom",
    description: "Track spend on any vendor billing page — Bynaus Edge reads it for you.",
    domain: "",
  },
] as const;

function IntegrationsCatalog({
  accounts,
  onConnect,
}: {
  accounts: AccountOut[];
  onConnect: (vendor: string) => void;
}) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Available integrations"
        subheader="Connect a vendor to start tracking spend automatically."
      />
      <CardContent sx={{ pt: 0 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
            gap: 1.5,
          }}
        >
          {VENDOR_CATALOG.map((v) => {
            const connected = accounts.filter(
              (a) => a.vendor === v.id && a.is_active,
            );
            const isConnected = connected.length > 0;
            return (
              <Box
                key={v.id}
                sx={{
                  border: 1,
                  borderColor: isConnected ? "success.light" : "divider",
                  borderRadius: 1.5,
                  p: 1.75,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  bgcolor: isConnected ? "action.hover" : "background.paper",
                  transition: "border-color 0.15s",
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      bgcolor: "background.paper",
                      border: 1,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      overflow: "hidden",
                    }}
                  >
                    {v.domain ? (
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${v.domain}&sz=128`}
                        alt={v.name}
                        width={28}
                        height={28}
                        style={{ objectFit: "contain" }}
                      />
                    ) : (
                      <AutoAwesomeIcon sx={{ width: 28, height: 28, color: "primary.main" }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                      <Typography variant="body2" fontWeight={700}>
                        {v.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={v.category}
                        sx={{ fontSize: "0.6rem", height: 15, px: 0 }}
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}
                    >
                      {v.description}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  {isConnected ? (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`${connected.length} account${connected.length > 1 ? "s" : ""} connected`}
                      sx={{ fontSize: "0.7rem" }}
                    />
                  ) : (
                    <Chip
                      size="small"
                      label="Not connected"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem", color: "text.disabled" }}
                    />
                  )}
                  <Button
                    size="small"
                    variant={isConnected ? "text" : "outlined"}
                    onClick={() => onConnect(v.id)}
                    sx={{ minWidth: 0, px: 1.25 }}
                  >
                    {isConnected ? "Add another" : "Connect"}
                  </Button>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── AI insights ──────────────────────────────────────────────────────────

const CATEGORY_LABEL: Record<Recommendation["category"], string> = {
  usage_pattern: "Usage pattern",
  wasted_capacity: "Wasted capacity",
  commitment_savings: "Commitment savings",
  caching_opportunity: "Caching opportunity",
  cheaper_tier: "Cheaper tier",
  mission_critical_risk: "Mission-critical risk",
  forecast_anomaly: "Forecast anomaly",
  billing_hygiene: "Billing hygiene",
};

function AiInsightsCard({ hasAccounts }: { hasAccounts: boolean }) {
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const analyze = useMutation({
    mutationFn: () =>
      api<AnalysisResult>("/api/vendor-watch/analyze", { method: "POST" }),
    onSuccess: (r) => { setResult(r); setError(null); },
    onError: (e) => { setError(errorMessage(e)); setResult(null); },
  });

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        avatar={<AutoAwesomeIcon sx={{ color: "primary.main" }} />}
        title="AI cost optimization"
        subheader="AI reviews your spend history and surfaces concrete savings — ranked by estimated monthly impact."
        action={
          <Button
            variant={result ? "outlined" : "contained"}
            startIcon={<AutoAwesomeIcon />}
            disabled={!hasAccounts || analyze.isPending}
            onClick={() => analyze.mutate()}
            size="small"
          >
            {analyze.isPending
              ? "Analyzing…"
              : result
                ? "Re-analyze"
                : "Analyze spend"}
          </Button>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {!hasAccounts && (
          <Typography variant="body2" color="text.secondary">
            Connect at least one vendor and wait for the first sync — there's no spend data to analyze yet.
          </Typography>
        )}
        {hasAccounts && !result && !analyze.isPending && !error && (
          <Typography variant="body2" color="text.secondary">
            Click <strong>Analyze spend</strong> to get AI recommendations. Takes ~10 seconds and costs about a cent in AI tokens.
          </Typography>
        )}
        {analyze.isPending && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              AI is reviewing your snapshots…
            </Typography>
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 0 }}>{error}</Alert>
        )}
        {result && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {result.summary}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="baseline">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Estimated monthly savings
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {dollars(result.estimated_total_savings_cents)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Recommendations
                  </Typography>
                  <Typography variant="h6">{result.recommendations.length}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Accounts reviewed
                  </Typography>
                  <Typography variant="h6">{result.accounts_analyzed}</Typography>
                </Box>
                <Box sx={{ ml: "auto" }}>
                  <Typography variant="caption" color="text.disabled">
                    {new Date(result.generated_at).toLocaleString()} · {result.model}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {result.recommendations.length === 0 ? (
              <Alert severity="success">
                No optimization opportunities found — your spend looks healthy.
              </Alert>
            ) : (
              <Stack spacing={1.25}>
                {result.recommendations.map((rec, i) => (
                  <RecommendationCard key={i} rec={rec} />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function RecommendationCard({ rec }: { rec: Recommendation }) {
  const severityBorder =
    rec.severity === "critical" ? "error.main"
    : rec.severity === "warning" ? "warning.main"
    : "info.main";
  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderLeftWidth: 4,
        borderLeftColor: severityBorder,
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {rec.title}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={CATEGORY_LABEL[rec.category]}
              sx={{ fontSize: "0.65rem", height: 18 }}
            />
            {rec.vendor && (
              <Chip
                size="small"
                label={`${rec.vendor}${rec.account_label ? "/" + rec.account_label : ""}`}
                sx={{ fontSize: "0.65rem", height: 18 }}
              />
            )}
            <Chip
              size="small"
              color={
                rec.confidence === "high" ? "success"
                : rec.confidence === "low" ? "default"
                : "primary"
              }
              variant="outlined"
              label={`${rec.confidence} confidence`}
              sx={{ fontSize: "0.65rem", height: 18 }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: rec.action ? 0.5 : 0 }}>
            {rec.rationale}
          </Typography>
          {rec.action && (
            <Typography variant="caption" color="text.primary" sx={{ display: "block", fontStyle: "italic" }}>
              → {rec.action}
            </Typography>
          )}
        </Box>
        {rec.estimated_monthly_savings_cents != null && (
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Save/mo
            </Typography>
            <Typography variant="subtitle1" color="success.main" fontWeight={700}>
              {dollars(rec.estimated_monthly_savings_cents)}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// ─── Key instructions ─────────────────────────────────────────────────────

const KEY_INSTRUCTIONS: Record<string, {
  url: string;
  label: string;
  steps: string[];
  warning?: string;
  bookmarklet?: string;
}> = {
  anthropic: {
    url: "https://console.anthropic.com/settings/admin-keys",
    label: "Open Anthropic Console",
    steps: [
      "Go to console.anthropic.com → Settings → Admin API Keys",
      "Click Create Key",
      "Copy the key — it starts with sk-ant-admin-",
    ],
    warning: "Do not use a regular API key (sk-ant-api-…). It won't have access to usage data.",
  },
  openai: {
    url: "https://platform.openai.com/settings/organization/api-keys",
    label: "Open OpenAI Platform",
    steps: [
      "Go to platform.openai.com → Organization settings → API keys",
      "Click Create new secret key → select type Admin",
      "Copy the key — it starts with sk-admin-",
    ],
    warning: "Do not use a project key (sk-proj-…). Only Admin keys can read org-level cost data.",
  },
  cloudflare: {
    url: "https://dash.cloudflare.com/profile/api-tokens",
    label: "Open Cloudflare Dashboard",
    steps: [
      "Go to dash.cloudflare.com → My Profile → API Tokens",
      "Click Create Token → use the 'Read billing information' template",
      "Click Continue to summary → Create Token",
      "Copy the token — it's only shown once",
    ],
    warning: "Do not use the Global API Key — it has too much access. Use a scoped API Token.",
  },
  google_ai_studio: {
    url: "https://console.cloud.google.com/iam-admin/serviceaccounts",
    label: "Open Google Cloud Console",
    steps: [
      "Go to console.cloud.google.com → IAM & Admin → Service Accounts",
      "Create a service account (or select an existing one)",
      "Grant it the Monitoring Viewer role on the project",
      "Click Keys → Add Key → JSON → Create",
      "Paste the entire downloaded JSON file below",
    ],
    warning: "A regular API key (AIza…) won't work — Google billing APIs require a service account. Tracks generativelanguage.googleapis.com (AI Studio / Gemini API tier).",
  },
  google_vertex_ai: {
    url: "https://console.cloud.google.com/iam-admin/serviceaccounts",
    label: "Open Google Cloud Console",
    steps: [
      "Go to console.cloud.google.com → IAM & Admin → Service Accounts",
      "Create a service account (or select an existing one)",
      "Grant it the Monitoring Viewer role on the project",
      "Click Keys → Add Key → JSON → Create",
      "Paste the entire downloaded JSON file below",
    ],
    warning: "A regular API key (AIza…) won't work — Google billing APIs require a service account. Tracks aiplatform.googleapis.com (Vertex AI / enterprise Gemini).",
  },
  cursor: {
    url: "https://www.cursor.com/settings",
    label: "Open Cursor Settings",
    steps: [
      "Go to cursor.com → Settings → Team → API keys",
      "Click Create API key (Admin key, not a personal token)",
      "Copy the key — it will only be shown once",
    ],
    warning: "Cursor uses Basic Auth — the key is the username with an empty password. Personal user tokens don't have billing scope.",
  },
  aws: {
    url: "https://console.aws.amazon.com/iam/home#/users",
    label: "Open AWS IAM Console",
    steps: [
      "Go to console.aws.amazon.com → IAM → Users → your user",
      "Click Security credentials → Create access key",
      "Choose 'Application running outside AWS' → Next → Create",
      "Copy the Access Key ID (AKIA…) and Secret Access Key",
      "Paste each into the separate fields below",
    ],
    warning: "The IAM user needs ce:GetCostAndUsage permission — attach 'AWSBillingReadOnlyAccess'. Cost Explorer must be enabled at console.aws.amazon.com/cost-management (one-time, takes ~24h).",
  },
  vapi: {
    url: "https://dashboard.vapi.ai",
    label: "Open VAPI Dashboard",
    steps: [
      "Go to dashboard.vapi.ai → Account Settings → API Keys",
      "Copy your Private Key (starts with a long token string)",
    ],
    warning: "Use the Private Key, not the Public (client-side) key. The private key has analytics read access.",
  },
  upwork: {
    url: "https://www.upwork.com",
    label: "Open Upwork",
    steps: [
      "Make sure Bynaus Edge is running (Settings → Integrations → Bynaus Edge)",
      "Install the Chrome extension and connect it to Edge",
      "Log into Upwork in that Chrome browser (just once — stays logged in)",
      "Hit 'Test — fetch live spend' to confirm Edge can read your Upwork spend",
    ],
    warning: "No API key needed. Edge navigates to Upwork in your browser and reads the spend using vision AI.",
  },
  stripe: {
    url: "https://dashboard.stripe.com/apikeys",
    label: "Open Stripe Dashboard",
    steps: [
      "Go to dashboard.stripe.com → Developers → API keys",
      "Under 'Restricted keys', click 'Create restricted key'",
      "Set 'Balance transactions' → Read",
      "Click 'Create key' and copy the rk_live_… token",
      "Or use your existing secret key (sk_live_…) for full access",
    ],
    warning: "Do not use a publishable key (pk_…) — it has no billing access. A restricted key with 'balance_transactions: Read' is the safest option.",
  },
  shopify: {
    url: "https://accounts.shopify.com/",
    label: "Open Shopify Admin",
    steps: [
      "Go to your Shopify Admin → Settings → Billing",
      "Bynaus Edge will read your plan charges and app fees directly from this page",
      "No API key needed — Edge uses your existing logged-in session",
      "Hit 'Test — fetch live spend' to confirm Edge can read your billing page",
    ],
    warning: "Shopify's merchant billing API is not publicly accessible. Edge uses vision AI to read the billing page from your already-logged-in browser session — zero OAuth setup required.",
  },
  chargebee: {
    url: "https://app.chargebee.com/",
    label: "Open Chargebee",
    steps: [
      "Log into your Chargebee site (e.g. acme.chargebee.com)",
      "Go to Settings → Configure Chargebee → API Keys",
      "Click 'Add API Key' and choose 'Read-Only Key' (or use Full-Access)",
      "Copy the key — it starts with your site's prefix",
      "Also note your site name (the subdomain before .chargebee.com)",
    ],
    warning: "You need both the site name (e.g. 'acme') and the API key. A Read-Only key is sufficient for billing sync.",
  },
};


function KeyInstructions({ vendor }: { vendor: string }) {
  const info = KEY_INSTRUCTIONS[vendor];
  if (!info) return null;
  return (
    <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}>
      <Stack spacing={0.75}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
            How to get this key
          </Typography>
          <Button
            size="small"
            variant="text"
            endIcon={<OpenInNewIcon sx={{ fontSize: "0.85rem !important" }} />}
            href={info.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: "0.75rem", py: 0, minWidth: 0 }}
          >
            {info.label}
          </Button>
        </Stack>
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          {info.steps.map((step, i) => (
            <Typography key={i} component="li" variant="caption" color="text.secondary" sx={{ mb: 0.25 }}>
              {step}
            </Typography>
          ))}
        </Box>
        {info.warning && (
          <Typography variant="caption" color="warning.main">
            ⚠ {info.warning}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

// ─── route ────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/_auth/settings/api-costs")({
  component: ApiCostsPage,
});

function ApiCostsPage() {
  const qc = useQueryClient();
  const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" | "info" } | null>(null);
  const [addVendor, setAddVendor] = useState<string | null>(null);
  const [editAccount, setEditAccount] = useState<AccountOut | null>(null);
  const [manualAccount, setManualAccount] = useState<AccountOut | null>(null);
  const [membersAccount, setMembersAccount] = useState<AccountOut | null>(null);
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [deleteAccount, setDeleteAccount] = useState<AccountOut | null>(null);

  const accountsQuery = useQuery({
    queryKey: ["vendor-watch", "accounts"],
    queryFn: () => api<{ accounts: AccountOut[]; count: number }>("/api/vendor-watch/accounts"),
    refetchInterval: 60_000, // refresh every minute
  });

  const syncOne = useMutation({
    mutationFn: (id: string) =>
      api(`/api/vendor-watch/accounts/${id}/sync`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-watch"] });
      setToast({ msg: "Sync complete", severity: "success" });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const syncAll = useMutation({
    mutationFn: () => api("/api/vendor-watch/sync-all", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-watch"] });
      setToast({ msg: "All accounts synced", severity: "success" });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const removeAccount = useMutation({
    mutationFn: (id: string) =>
      api(`/api/vendor-watch/accounts/${id}`, { method: "DELETE" }),
    onSuccess: (_data, _id) => {
      qc.invalidateQueries({ queryKey: ["vendor-watch"] });
      setToast({
        msg: `Removed ${deleteAccount?.vendor}/${deleteAccount?.account_label}`,
        severity: "info",
      });
      setDeleteAccount(null);
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const accounts = accountsQuery.data?.accounts ?? [];
  const allAlerts = accounts.flatMap((a) =>
    a.active_alerts.map((al) => ({ ...al, account: `${a.vendor}/${a.account_label}`, critical: a.is_mission_critical }))
  );
  const totalMtdCents = accounts.reduce((sum, a) => sum + (a.latest_snapshot?.amount_cents ?? 0), 0);

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5">API Costs</Typography>
          <Typography variant="body2" color="text.secondary">
            Watch Anthropic, OpenAI, and other vendor spend across all your accounts.
            Mission-critical accounts get tighter alert thresholds.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<SyncIcon />}
            onClick={() => syncAll.mutate()}
            disabled={syncAll.isPending || accounts.length === 0}
            variant="outlined"
          >
            {syncAll.isPending ? "Syncing…" : "Sync all"}
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setAddVendor("anthropic")}
            variant="contained"
          >
            Add account
          </Button>
        </Stack>
      </Stack>

      {/* Alerts banner */}
      {allAlerts.length > 0 && (
        <Card sx={{ mb: 3, borderLeft: 4, borderColor: allAlerts.some((a) => a.severity === "critical") ? "error.main" : "warning.main" }}>
          <CardHeader
            avatar={allAlerts.some((a) => a.severity === "critical") ? <ErrorIcon color="error" /> : <WarningIcon color="warning" />}
            title={
              <Typography variant="subtitle1">
                {allAlerts.length} active alert{allAlerts.length === 1 ? "" : "s"}
                {allAlerts.some((a) => a.severity === "critical") && (
                  <Chip size="small" color="error" label={`${allAlerts.filter((a) => a.severity === "critical").length} critical`} sx={{ ml: 1 }} />
                )}
              </Typography>
            }
            action={
              <IconButton
                size="small"
                onClick={() => setAlertsExpanded((v) => !v)}
                aria-label={alertsExpanded ? "Collapse alerts" : "Expand alerts"}
              >
                {alertsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
            onClick={() => setAlertsExpanded((v) => !v)}
            sx={{ cursor: "pointer" }}
          />
          {alertsExpanded && (
            <CardContent sx={{ pt: 0 }}>
              <Stack spacing={1}>
                {allAlerts.map((a, i) => (
                  <Alert key={i} severity={severityColor(a.severity) as any}>
                    <strong>{a.account}{a.critical && " ⚠️"}</strong> — {a.message}
                  </Alert>
                ))}
              </Stack>
            </CardContent>
          )}
        </Card>
      )}

      {/* Integrations catalog */}
      <IntegrationsCatalog accounts={accounts} onConnect={(v) => setAddVendor(v)} />

      {/* AI cost optimization */}
      <AiInsightsCard hasAccounts={accounts.length > 0} />

      {/* Headline tile */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={4} alignItems="baseline">
            <Box>
              <Typography variant="caption" color="text.secondary">Total MTD spend</Typography>
              <Typography variant="h4">{dollars(totalMtdCents)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Accounts monitored</Typography>
              <Typography variant="h6">{accounts.length}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Mission-critical</Typography>
              <Typography variant="h6">{accounts.filter((a) => a.is_mission_critical).length}</Typography>
            </Box>
            <Box sx={{ ml: "auto" }}>
              <Typography variant="caption" color="text.secondary">
                {accountsQuery.dataUpdatedAt ? `Refreshed ${ago(new Date(accountsQuery.dataUpdatedAt).toISOString())}` : ""}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Accounts table */}
      <Card>
        <CardHeader title="Monitored accounts" />
        <CardContent sx={{ p: 0 }}>
          {accountsQuery.isLoading ? (
            <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
          ) : accounts.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">No accounts monitored yet.</Typography>
              <Button onClick={() => setAddVendor("anthropic")} startIcon={<AddIcon />} sx={{ mt: 2 }}>
                Add your first account
              </Button>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">MTD spend</TableCell>
                  <TableCell align="right">Forecast / Budget</TableCell>
                  <TableCell align="right">Next bill</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="right">Runway</TableCell>
                  <TableCell>Last sync</TableCell>
                  <TableCell>Alerts</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((a) => {
                  const ar = a.auto_recharge_view;
                  const overBudget = (ar.variance_vs_budget_pct ?? 0) > 20;
                  return (
                    <TableRow key={a.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2"><strong>{a.vendor}</strong>/{a.account_label}</Typography>
                          {a.is_mission_critical && (
                            <Chip size="small" color="error" label="MISSION-CRITICAL" sx={{ fontSize: "0.65rem" }} />
                          )}
                          {!a.is_active && <Chip size="small" label="inactive" />}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                          <Typography variant="body2">{dollars(a.latest_snapshot?.amount_cents)}</Typography>
                          {a.latest_snapshot?._warning && (
                            <Tooltip title={a.latest_snapshot._warning} arrow>
                              <WarningIcon fontSize="small" color="warning" sx={{ fontSize: 14, cursor: "help" }} />
                            </Tooltip>
                          )}
                        </Stack>
                        {a.latest_snapshot?.daily_burn_cents != null && (
                          <Typography variant="caption" color="text.secondary">
                            {dollars(a.latest_snapshot.daily_burn_cents)}/day
                          </Typography>
                        )}
                        {a.latest_snapshot?._warning && !a.latest_snapshot.amount_cents && (
                          <Typography variant="caption" color="warning.main" sx={{ display: "block", maxWidth: 160 }}>
                            Setup needed
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={overBudget ? "error.main" : "text.primary"}>
                          {dollars(ar.forecast_month_cents)} / {dollars(a.monthly_budget_cents)}
                        </Typography>
                        {ar.variance_vs_budget_pct != null && (
                          <Typography variant="caption" color={overBudget ? "error.main" : "text.secondary"}>
                            {pct(ar.variance_vs_budget_pct)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {a.latest_snapshot?.next_billing_date
                          ? <BillingDateCell dateStr={a.latest_snapshot.next_billing_date} />
                          : <Typography variant="body2" color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{dollars(a.credit_balance_cents)}</Typography>
                        {a.credit_balance_updated_at && (
                          <Typography variant="caption" color="text.secondary">
                            {ago(a.credit_balance_updated_at)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {a.latest_snapshot?.projected_days_to_zero != null
                            ? `${a.latest_snapshot.projected_days_to_zero.toFixed(1)}d`
                            : "—"}
                        </Typography>
                        {ar.charges_this_month != null && (
                          <Typography variant="caption" color="text.secondary">
                            {ar.charges_this_month} charges
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={a.last_sync_error || ""}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Chip
                              size="small"
                              color={a.last_sync_status === "error" ? "error" : a.last_sync_status === "ok" ? "success" : "default"}
                              label={a.last_sync_status}
                            />
                            <Typography variant="caption" color="text.secondary">{ago(a.last_synced_at)}</Typography>
                          </Stack>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {a.active_alerts.length === 0 ? (
                          <Chip size="small" label="clear" variant="outlined" />
                        ) : (
                          a.active_alerts.map((al, i) => (
                            <Chip
                              key={i}
                              size="small"
                              color={severityColor(al.severity) as any}
                              label={al.alert_type}
                              sx={{ mr: 0.5, fontSize: "0.65rem" }}
                            />
                          ))
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {VENDORS_WITH_MEMBERS.includes(a.vendor) && (
                          <Tooltip title="Team spend breakdown">
                            <IconButton size="small" onClick={() => setMembersAccount(a)}>
                              <PeopleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {a.vendor === "upwork" && (
                          <Tooltip title="Enter spend manually">
                            <IconButton size="small" onClick={() => setManualAccount(a)}>
                              <EditIcon fontSize="small" sx={{ color: "warning.main" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Sync now">
                          <IconButton size="small" onClick={() => syncOne.mutate(a.id)}>
                            <SyncIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setEditAccount(a)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" onClick={() => setDeleteAccount(a)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {addVendor !== null && (
        <AddAccountDialog
          initialVendor={addVendor}
          onClose={() => setAddVendor(null)}
          onResult={(r) => setToast(r)}
        />
      )}
      {membersAccount && (
        <MembersDrawer
          account={membersAccount}
          onClose={() => setMembersAccount(null)}
        />
      )}
      {editAccount && (
        <EditAccountDialog
          account={editAccount}
          onClose={() => setEditAccount(null)}
          onResult={(r) => setToast(r)}
        />
      )}
      {manualAccount && (
        <ManualSnapshotDialog
          account={manualAccount}
          onClose={() => setManualAccount(null)}
          onResult={(r) => { setToast(r); setManualAccount(null); }}
        />
      )}

      {/* Remove-account confirmation. Permanent delete — stops syncs,
          drops snapshots/alerts. AI auto-recharge inference loses history;
          this is fine because re-adding the vendor restarts that learning. */}
      <Dialog
        open={!!deleteAccount}
        onClose={() => !removeAccount.isPending && setDeleteAccount(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove monitored account?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This stops syncing <strong>{deleteAccount?.vendor}/{deleteAccount?.account_label}</strong> and deletes all stored snapshots, alerts, and the encrypted API key.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            The vendor's billing data itself is not affected — only this monitor is removed. You can re-add it later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAccount(null)} disabled={removeAccount.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={removeAccount.isPending}
            onClick={() => deleteAccount && removeAccount.mutate(deleteAccount.id)}
          >
            {removeAccount.isPending ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? <Alert severity={toast.severity}>{toast.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}

// ─── Add account dialog ───────────────────────────────────────────────────

function AddAccountDialog({
  onClose,
  onResult,
  initialVendor = "anthropic",
}: {
  onClose: () => void;
  onResult: (r: { msg: string; severity: "success" | "error" | "info" }) => void;
  initialVendor?: string;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    vendor: initialVendor,
    account_label: "",
    admin_api_key: "",
    aws_key_id: "",
    aws_secret: "",
    bigquery_dataset: "",
    upwork_company_ref: "",
    custom_vendor_name: "",
    rpa_billing_url: "",
    is_mission_critical: false,
    credit_balance: "",
    auto_recharge_amount: "100",
    monthly_budget: "",
  });
  const [showKey, setShowKey] = useState(false);

  // custom vendor: the actual vendor id stored is the user-typed name (lowercased/slugified)
  const isCustomVendor = form.vendor === "__custom__";
  const resolvedVendorId = isCustomVendor
    ? (form.custom_vendor_name.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "_") || "__custom__")
    : form.vendor;

  // For AWS the two fields are combined before submission.
  // For custom/RPA vendors there is no API key — the billing URL drives the adapter.
  const effectiveApiKey =
    form.vendor === "upwork" || isCustomVendor
      ? "edge-rpa"
      : form.vendor === "aws"
        ? form.aws_key_id.trim() && form.aws_secret.trim()
          ? `${form.aws_key_id.trim()}:${form.aws_secret.trim()}`
          : ""
        : form.admin_api_key;
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    message?: string;
    error?: string;
    hint?: string;
  } | null>(null);

  const testKey = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; message?: string; error?: string; hint?: string }>(
        "/api/vendor-watch/test-credentials",
        {
          method: "POST",
          body: {
            vendor: resolvedVendorId,
            admin_api_key: effectiveApiKey,
            bigquery_dataset: form.bigquery_dataset || null,
            rpa_billing_url: isCustomVendor ? (form.rpa_billing_url || null) : null,
          },
        },
      ),
    onSuccess: (r) => setTestResult(r),
    onError: (e) => setTestResult({ ok: false, error: errorMessage(e) }),
  });

  const create = useMutation({
    mutationFn: () =>
      api("/api/vendor-watch/accounts", {
        method: "POST",
        body: {
          vendor: resolvedVendorId,
          account_label: form.account_label,
          admin_api_key: effectiveApiKey,
          is_mission_critical: form.is_mission_critical,
          credit_balance_cents: form.credit_balance ? Math.round(parseFloat(form.credit_balance) * 100) : null,
          auto_recharge_amount_cents: form.auto_recharge_amount ? Math.round(parseFloat(form.auto_recharge_amount) * 100) : null,
          monthly_budget_cents: form.monthly_budget ? Math.round(parseFloat(form.monthly_budget) * 100) : null,
          external_org_id: form.vendor === "upwork" ? (form.upwork_company_ref || null) : (form.bigquery_dataset || null),
          rpa_billing_url: isCustomVendor ? (form.rpa_billing_url || null) : null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-watch"] });
      onResult({ msg: `Added ${resolvedVendorId}/${form.account_label}`, severity: "success" });
      onClose();
    },
    onError: (e) => onResult({ msg: errorMessage(e), severity: "error" }),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add API cost monitor</DialogTitle>
      <DialogContent>
        {/* form wrapper suppresses browser save-password heuristics */}
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Vendor</InputLabel>
            <Select
              label="Vendor"
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
            >
              <MenuItem value="anthropic">Anthropic</MenuItem>
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="aws">AWS</MenuItem>
              <MenuItem value="cloudflare">Cloudflare</MenuItem>
              <MenuItem value="google_ai_studio">Google AI Studio</MenuItem>
              <MenuItem value="google_vertex_ai">Google Vertex AI</MenuItem>
              <MenuItem value="cursor">Cursor</MenuItem>
              <MenuItem value="vapi">VAPI.ai</MenuItem>
              <MenuItem value="upwork">Upwork</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Account label"
            placeholder="production"
            value={form.account_label}
            onChange={(e) => setForm({ ...form, account_label: e.target.value })}
            helperText="A name for this account — e.g. 'production', 'staging'."
          />
          {isCustomVendor && (
            <>
              <TextField
                size="small"
                label="Vendor name"
                placeholder="Notion, Figma, Linear, Heroku…"
                value={form.custom_vendor_name}
                onChange={(e) => setForm({ ...form, custom_vendor_name: e.target.value })}
                helperText="The name of the vendor you want to track."
                inputProps={{ spellCheck: false, autoComplete: "off" }}
              />
              <TextField
                size="small"
                label="Billing page URL"
                placeholder="https://app.example.com/settings/billing"
                value={form.rpa_billing_url}
                onChange={(e) => { setForm({ ...form, rpa_billing_url: e.target.value }); setTestResult(null); }}
                helperText="Bynaus Edge will navigate Chrome to this URL and read the spend amount. You must be logged in."
                inputProps={{ spellCheck: false, autoComplete: "off" }}
              />
              <Alert severity="info" sx={{ py: 0.5 }}>
                No API key needed — Bynaus Edge navigates to the billing page in your Chrome browser and reads the spend using vision AI.
                Make sure Edge is running and you are logged into this vendor in Chrome.
              </Alert>
            </>
          )}
          <KeyInstructions vendor={form.vendor} />
          {form.vendor === "google_ai_studio" && (
            <TextField
              size="small"
              label="BigQuery billing dataset"
              placeholder="gen-lang-client-0855449492.billing_export"
              value={form.bigquery_dataset}
              onChange={(e) => setForm({ ...form, bigquery_dataset: e.target.value })}
              helperText={
                form.bigquery_dataset
                  ? "✓ Will query BigQuery for real billing data"
                  : "Optional but recommended — enables real spend data. Format: project_id.dataset_id. Enable in GCP → Billing → Billing export → BigQuery export."
              }
              inputProps={{ spellCheck: false, autoComplete: "off" }}
            />
          )}
          {form.vendor === "aws" ? (
            <>
              <TextField
                size="small"
                label="Access Key ID"
                placeholder="AKIAIOSFODNN7EXAMPLE"
                value={form.aws_key_id}
                onChange={(e) => { setForm({ ...form, aws_key_id: e.target.value }); setTestResult(null); }}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: false,
                  "data-1p-ignore": "",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                  name: "aws-access-key-id",
                }}
              />
              <TextField
                size="small"
                label="Secret Access Key"
                placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                value={form.aws_secret}
                onChange={(e) => { setForm({ ...form, aws_secret: e.target.value }); setTestResult(null); }}
                sx={{ "& input": { WebkitTextSecurity: showKey ? "none" : "disc" } as any }}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: false,
                  "data-1p-ignore": "",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                  name: "aws-secret-access-key",
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowKey((v) => !v)} tabIndex={-1} edge="end">
                        {showKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          ) : form.vendor === "upwork" ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              No API key needed — Bynaus Edge reads your Upwork spend directly from your browser session.
              Make sure Edge is running and you're logged into Upwork in Chrome.
            </Alert>
          ) : isCustomVendor ? null : (
            <TextField
              size="small"
              label={["google_ai_studio", "google_vertex_ai"].includes(form.vendor) ? "Credentials JSON" : "Admin API key"}
              placeholder={
                form.vendor === "openai" ? "sk-admin-..." :
                form.vendor === "cursor" ? "cursor_..." :
                form.vendor === "vapi" ? "vapi private key..." :
                ["google_ai_studio", "google_vertex_ai"].includes(form.vendor) ? '{"type":"service_account","project_id":"..."}' :
                "sk-ant-admin-..."
              }
              type="text"
              multiline={["google_ai_studio", "google_vertex_ai"].includes(form.vendor)}
              minRows={["google_ai_studio", "google_vertex_ai"].includes(form.vendor) ? 4 : undefined}
              maxRows={["google_ai_studio", "google_vertex_ai"].includes(form.vendor) ? 8 : undefined}
              value={form.admin_api_key}
              onChange={(e) => { setForm({ ...form, admin_api_key: e.target.value }); setTestResult(null); }}
              sx={["google_ai_studio", "google_vertex_ai"].includes(form.vendor) ? { fontFamily: "monospace" } : { "& input": { WebkitTextSecurity: showKey ? "none" : "disc" } as any }}
              inputProps={{
                autoComplete: "off",
                autoCorrect: "off",
                autoCapitalize: "off",
                spellCheck: false,
                "data-1p-ignore": "",
                "data-lpignore": "true",
                "data-form-type": "other",
                name: "vendor-admin-token",
              }}
              InputProps={["google_ai_studio", "google_vertex_ai"].includes(form.vendor) ? {} : {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowKey((v) => !v)} tabIndex={-1} edge="end">
                      {showKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              disabled={
                testKey.isPending ||
                (isCustomVendor ? !form.rpa_billing_url.trim() : !effectiveApiKey)
              }
              onClick={() => testKey.mutate()}
            >
              {testKey.isPending
                ? (form.vendor === "upwork" || isCustomVendor ? "Fetching spend…" : "Testing…")
                : (form.vendor === "upwork" || isCustomVendor ? "Test — fetch live spend" : "Test key")}
            </Button>
            {testResult && (
              <Alert
                severity={testResult.ok ? "success" : "error"}
                sx={{ flex: 1, py: 0 }}
              >
                {testResult.ok
                  ? testResult.message
                  : (
                      <>
                        {testResult.error}
                        {testResult.hint && (
                          <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.85 }}>
                            {testResult.hint}
                          </Typography>
                        )}
                      </>
                    )}
              </Alert>
            )}
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={form.is_mission_critical}
                onChange={(e) => setForm({ ...form, is_mission_critical: e.target.checked })}
              />
            }
            label="Mission-critical (tighter alert thresholds, critical severity)"
          />
          <TextField
            size="small"
            label="Current credit balance"
            type="number"
            value={form.credit_balance}
            onChange={(e) => setForm({ ...form, credit_balance: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText="Check the Anthropic console for the current pre-paid balance. Enables runway alerts."
          />
          <TextField
            size="small"
            label="Auto-recharge amount"
            type="number"
            value={form.auto_recharge_amount}
            onChange={(e) => setForm({ ...form, auto_recharge_amount: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText="Anthropic default is $100 per top-up. Drives charge cadence tracking."
          />
          <TextField
            size="small"
            label="Monthly budget"
            type="number"
            value={form.monthly_budget}
            onChange={(e) => setForm({ ...form, monthly_budget: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText="Forecast-overrun alert fires when projected month-end spend exceeds this by 20%."
          />
        </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            !form.account_label ||
            (isCustomVendor
              ? !form.custom_vendor_name.trim() || !form.rpa_billing_url.trim()
              : (!effectiveApiKey && form.vendor !== "upwork")) ||
            create.isPending
          }
          onClick={() => create.mutate()}
        >
          {create.isPending ? "Adding…" : "Add account"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit dialog ──────────────────────────────────────────────────────────


function TestStoredKeyButton({ accountId }: { accountId: string }) {
  const [result, setResult] = useState<{ ok: boolean; message?: string; error?: string; hint?: string } | null>(null);
  const test = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; message?: string; error?: string; hint?: string }>(
        `/api/vendor-watch/accounts/${accountId}/test`,
        { method: "POST" },
      ),
    onSuccess: (r) => setResult(r),
    onError: (e) => setResult({ ok: false, error: errorMessage(e) }),
  });
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button size="small" variant="outlined" disabled={test.isPending} onClick={() => test.mutate()}>
        {test.isPending ? "Testing…" : "Test stored key"}
      </Button>
      {result && (
        <Alert severity={result.ok ? "success" : "error"} sx={{ flex: 1, py: 0 }}>
          {result.ok ? result.message : (
            <>
              {result.error}
              {result.hint && (
                <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.85 }}>
                  {result.hint}
                </Typography>
              )}
            </>
          )}
        </Alert>
      )}
    </Stack>
  );
}


function ManualSnapshotDialog({
  account,
  onClose,
  onResult,
}: {
  account: AccountOut;
  onClose: () => void;
  onResult: (r: { msg: string; severity: "success" | "error" }) => void;
}) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const save = useMutation({
    mutationFn: () =>
      api(`/api/vendor-watch/accounts/${account.id}/manual-snapshot`, {
        method: "POST",
        body: { amount_dollars: parseFloat(amount) },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-watch"] });
      onResult({ msg: "Spend recorded", severity: "success" });
    },
    onError: (e: any) => onResult({ msg: e.message ?? "Failed", severity: "error" }),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Enter Upwork MTD spend</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Check your total at <strong>Upwork → Reports → Transaction History → Charges tab</strong>,
          then enter the month-to-date total below.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Month-to-date spend"
          placeholder="4789.50"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          inputProps={{ inputMode: "decimal" }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!amount || isNaN(parseFloat(amount)) || save.isPending}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function EditAccountDialog({
  account,
  onClose,
  onResult,
}: {
  account: AccountOut;
  onClose: () => void;
  onResult: (r: { msg: string; severity: "success" | "error" | "info" }) => void;
}) {
  const qc = useQueryClient();
  const [showNewKey, setShowNewKey] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const remove = useMutation({
    mutationFn: () =>
      api(`/api/vendor-watch/accounts/${account.id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-watch"] });
      onResult({ msg: `Removed ${account.vendor}/${account.account_label}`, severity: "info" });
      onClose();
    },
    onError: (e) => onResult({ msg: errorMessage(e), severity: "error" }),
  });
  const [form, setForm] = useState({
    credit_balance: account.credit_balance_cents != null ? (account.credit_balance_cents / 100).toString() : "",
    monthly_budget: account.monthly_budget_cents != null ? (account.monthly_budget_cents / 100).toString() : "",
    auto_recharge_amount: account.auto_recharge_amount_cents != null ? (account.auto_recharge_amount_cents / 100).toString() : "",
    is_mission_critical: account.is_mission_critical,
    is_active: account.is_active,
    new_api_key: "",
    aws_new_key_id: "",
    aws_new_secret: "",
  });

  const update = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        credit_balance_cents: form.credit_balance ? Math.round(parseFloat(form.credit_balance) * 100) : null,
        monthly_budget_cents: form.monthly_budget ? Math.round(parseFloat(form.monthly_budget) * 100) : null,
        auto_recharge_amount_cents: form.auto_recharge_amount ? Math.round(parseFloat(form.auto_recharge_amount) * 100) : null,
        is_mission_critical: form.is_mission_critical,
        is_active: form.is_active,
      };
      if (account.vendor === "aws") {
        if (form.aws_new_key_id.trim() && form.aws_new_secret.trim()) {
          body.admin_api_key = `${form.aws_new_key_id.trim()}:${form.aws_new_secret.trim()}`;
        }
      } else if (form.new_api_key) {
        body.admin_api_key = form.new_api_key;
      }
      return api(`/api/vendor-watch/accounts/${account.id}`, { method: "PATCH", body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-watch"] });
      onResult({ msg: "Account updated", severity: "success" });
      onClose();
    },
    onError: (e) => onResult({ msg: errorMessage(e), severity: "error" }),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit {account.vendor}/{account.account_label}
      </DialogTitle>
      <DialogContent>
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="Current credit balance"
            type="number"
            value={form.credit_balance}
            onChange={(e) => setForm({ ...form, credit_balance: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText="Update whenever you check the Anthropic console — this is the only way runway alerts know what's available."
          />
          <TextField
            size="small"
            label="Monthly budget"
            type="number"
            value={form.monthly_budget}
            onChange={(e) => setForm({ ...form, monthly_budget: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />
          <TextField
            size="small"
            label="Auto-recharge amount"
            type="number"
            value={form.auto_recharge_amount}
            onChange={(e) => setForm({ ...form, auto_recharge_amount: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_mission_critical}
                onChange={(e) => setForm({ ...form, is_mission_critical: e.target.checked })}
              />
            }
            label="Mission-critical"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
            }
            label="Active (uncheck to pause monitoring)"
          />
          {account.vendor === "aws" ? (
            <>
              <TextField
                size="small"
                label="New Access Key ID (optional)"
                placeholder="AKIAIOSFODNN7EXAMPLE"
                value={form.aws_new_key_id}
                onChange={(e) => setForm({ ...form, aws_new_key_id: e.target.value })}
                helperText="Leave both blank to keep the existing key. Fill both to rotate."
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: false,
                  "data-1p-ignore": "",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                  name: "aws-new-access-key-id",
                }}
              />
              <TextField
                size="small"
                label="New Secret Access Key (optional)"
                placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                value={form.aws_new_secret}
                onChange={(e) => setForm({ ...form, aws_new_secret: e.target.value })}
                sx={{ "& input": { WebkitTextSecurity: showNewKey ? "none" : "disc" } as any }}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: false,
                  "data-1p-ignore": "",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                  name: "aws-new-secret-access-key",
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowNewKey((v) => !v)} tabIndex={-1} edge="end">
                        {showNewKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          ) : (
            <TextField
              size="small"
              label="New admin API key (optional)"
              type="text"
              value={form.new_api_key}
              onChange={(e) => setForm({ ...form, new_api_key: e.target.value })}
              helperText="Leave blank to keep the existing key. Use to rotate."
              sx={{ "& input": { WebkitTextSecurity: showNewKey ? "none" : "disc" } as any }}
              inputProps={{
                autoComplete: "off",
                autoCorrect: "off",
                autoCapitalize: "off",
                spellCheck: false,
                "data-1p-ignore": "",
                "data-lpignore": "true",
                "data-form-type": "other",
                name: "vendor-admin-token-rotate",
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowNewKey((v) => !v)} tabIndex={-1} edge="end">
                      {showNewKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <TestStoredKeyButton accountId={account.id} />
        </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        {confirmDelete ? (
          <>
            <Typography variant="body2" color="error.main" sx={{ ml: 1 }}>
              Remove this account?
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setConfirmDelete(false)}>No, keep it</Button>
              <Button
                size="small"
                color="error"
                variant="contained"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {remove.isPending ? "Removing…" : "Yes, remove"}
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Button color="error" size="small" onClick={() => setConfirmDelete(true)}>
              Remove
            </Button>
            <Stack direction="row" spacing={1}>
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="contained" disabled={update.isPending} onClick={() => update.mutate()}>
                {update.isPending ? "Saving…" : "Save"}
              </Button>
            </Stack>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
